import type { MeResponse, Permission, Workspace } from "@salon-pos/shared";
import { isFullPlanOnly, workspacesFor } from "@salon-pos/shared";
import type { MessageKey } from "./i18n/en";
import { ownerMode, type OwnerMode } from "./lib/ownerMode";

/** Native (Capacitor Android/iOS) builds are store-operations only: no owner portal, billing or platform admin. */
const IS_NATIVE = import.meta.env.VITE_CLIENT === "native";
/** The store app has ONE workspace (checkout paths) for every role; what each role sees is decided by NATIVE_NAV permissions. */
export const NATIVE_WORKSPACES: Workspace[] = ["checkout"];

/** Owner/manager sidebar groups (Task L). "daily" has no header; "pinned" (Settings) sits at the bottom of the sidebar. */
export type NavGroup = "daily" | "customers" | "catalog" | "team" | "stock" | "finance" | "online" | "pinned";
/** Consistent line-icon set (components/NavIcon.tsx). */
export type NavIconName =
  | "overview" | "calendar" | "register" | "orders" | "approvals" | "customers" | "membership" | "wallet" | "campaigns"
  | "services" | "ticket" | "receipt" | "team" | "payroll" | "box" | "clipboard" | "truck" | "payables" | "dailyClose"
  | "expenses" | "reports" | "globe" | "store" | "settings" | "addons" | "cash" | "sun" | "building" | "clock" | "camera" | "chart" | "home" | "more" | "repeat" | "whatsapp";
/** locked (Task W): a Full-plan-only module the salon doesn't have — shown to the owner with a "Full" badge, opens the upgrade note */
/** industry: only for salons with this business type (e.g. "massage" → 轮钟 rotation board); everyone else never sees it */
export interface NavItem { path: string; label: MessageKey; perm?: Permission; feature?: string; group?: NavGroup; icon: NavIconName; locked?: boolean; industry?: string }

/**
 * (Task O/P) The native store app (Android / iOS / iPad) shows ONLY these sections, filtered by the role's permissions,
 * in this order. Home (= Performance dashboard) is first. Short tab labels (Home / POS / Bookings / Clients …) so
 * nothing is truncated; the "More" sheet uses the longer names (NATIVE_LONG_LABEL).
 * One BOTTOM tab bar on every size (phones and tablets / iPad), max 5 visible tabs (components/Shell.tsx NativeShell).
 */
export const NATIVE_NAV: NavItem[] = [
  { path: "/checkout/home", label: "tp.tab.home", icon: "home" },
  { path: "/checkout/pos", label: "tp.tab.pos", perm: "sales.create", icon: "register" },
  // (massage shops) 轮钟 board right next to POS — only salons whose business types include massage
  { path: "/checkout/rotation", label: "rot.tab", perm: "appointments.view", industry: "massage", icon: "repeat" },
  { path: "/checkout/appointments", label: "tp.tab.bookings", perm: "appointments.view", icon: "calendar" },
  { path: "/checkout/customers", label: "tp.tab.clients", perm: "customers.manage", icon: "customers" },
  // (Task W) attendance / clock-in is a Full-plan feature
  { path: "/checkout/attendance", label: "tp.tab.clock", feature: "attendance", icon: "clock" },
  { path: "/checkout/photos", label: "tp.tab.photos", perm: "customers.view", icon: "camera" },
  // (Task EE) staff self-service hub (my customers, performance, payslips, claims, leave, bank details)
  { path: "/checkout/me", label: "ee.me.title", icon: "wallet" },
  { path: "/checkout/requests", label: "ee.me.requests", perm: "requests.approve", feature: "payroll", icon: "approvals" },
  { path: "/checkout/cash", label: "tp.tab.cash", perm: "cash_session.operate", icon: "cash" },
  // (Task DR) 欠款 list (who may see it is a shop setting; the page shows the API's answer)
  { path: "/checkout/debts", label: "dr.nav.debts", perm: "customers.manage", icon: "wallet" },
];
/**
 * (Task AN) CASHIER logins only (收银 account): 首页 → 收银 → 预约 → 顾客 → 配套顾客列表 → 订阅顾客列表 → 更多 (the rest, incl.
 * 欠款 and 日结 = the cash drawer close). Staff / managers keep NATIVE_NAV exactly as before.
 */
export const CASHIER_EXTRA: NavItem[] = [
  { path: "/checkout/package-customers", label: "an.nav.pkgCustomers", perm: "customers.view", feature: "packages", icon: "ticket" },
  { path: "/checkout/subscriptions", label: "an.nav.subs", perm: "subscriptions.view", feature: "subscriptions", icon: "repeat" },
];
const CASHIER_ORDER = ["/checkout/home", "/checkout/pos", "/checkout/rotation", "/checkout/appointments", "/checkout/customers", "/checkout/package-customers", "/checkout/subscriptions", "/checkout/debts", "/checkout/cash"];

/**
 * (Task AN) OWNER on the store app: an all-branch mobile dashboard — 首页 · 员工 · 库存 · 报销 · 请假, plus 更多 (the store
 * sections + "switch to cashier mode"). Paths stay under /checkout/ (the app bundle never carries /owner/ routes).
 */
export const OWNER_APP_NAV: NavItem[] = [
  { path: "/checkout/hq", label: "an.nav.hqHome", icon: "overview" },
  { path: "/checkout/hq/staff", label: "an.nav.hqStaff", icon: "team" },
  { path: "/checkout/hq/stock", label: "an.nav.hqStock", icon: "box" },
  { path: "/checkout/hq/claims", label: "an.nav.hqClaims", feature: "payroll", icon: "expenses" },
  { path: "/checkout/hq/leave", label: "an.nav.hqLeave", feature: "payroll", icon: "calendar" },
];
/** "More" sheet entry that flips the owner between the dashboard and the store tabs (handled by the shell, not a route) */
export const MODE_SWITCH = "#owner-mode";

/** "This device" (app lock, notifications, server, setup again): always the last entry under More. */
export const NATIVE_DEVICE: NavItem = { path: "/checkout/device", label: "tp.tab.device", icon: "settings" };
/** Longer names for the More sheet (it has room for them). */
export const NATIVE_LONG_LABEL: Partial<Record<string, MessageKey>> = {
  "/checkout/attendance": "tp.more.attendance", "/checkout/photos": "tp.more.photos", "/checkout/cash": "tp.more.cash",
  "/checkout/appointments": "to.nav.appointments", "/checkout/customers": "to.nav.customers", "/checkout/rotation": "rot.nav",
  "/checkout/package-customers": "an.nav.pkgCustomersLong", "/checkout/subscriptions": "an.nav.subsLong",
};
/** At most this many tabs are visible in the bottom bar (including "More"). */
export const MAX_TABS = 5;

/** Headed, collapsible groups in display order (between the header-less daily items and the pinned Settings). */
export const NAV_GROUPS: Array<{ id: Exclude<NavGroup, "daily" | "pinned">; label: MessageKey }> = [
  { id: "customers", label: "nav.group.customers" },
  { id: "catalog", label: "nav.group.catalog" },
  { id: "team", label: "nav.group.team" },
  { id: "stock", label: "nav.group.stock" },
  { id: "finance", label: "nav.group.finance" },
  { id: "online", label: "nav.group.online" },
];

/**
 * Owner/manager navigation (Task L): a few header-less daily items, six collapsible groups and Settings pinned at the
 * bottom, plus one "Add-ons" entry. Modules that are not in the salon's plan/add-ons are HIDDEN from the sidebar and
 * listed on the Add-ons page instead. Items are also filtered by the role's permission level.
 * Billing/subscription lives ONLY in the owner web portal — never in checkout (native POS app).
 */
export const NAV: Record<Workspace, NavItem[]> = {
  // compiled out of the native store app (constant-folded): no owner portal / billing paths in the bundle
  owner: IS_NATIVE ? [] : [
    { path: "/owner/overview", label: "nav.overview", perm: "reports.view", group: "daily", icon: "overview" },
    { path: "/owner/appointments", label: "nav.appointments", perm: "appointments.view", group: "daily", icon: "calendar" },
    { path: "/owner/pos", label: "nav.pos", perm: "sales.create", group: "daily", icon: "register" },
    { path: "/owner/rotation", label: "rot.nav", perm: "appointments.view", industry: "massage", group: "daily", icon: "repeat" },
    { path: "/owner/online-orders", label: "nav.onlineOrders", perm: "sales.create", feature: "shop", group: "daily", icon: "orders" },
    { path: "/owner/approvals", label: "nav.approvals", perm: "refunds.approve", group: "daily", icon: "approvals" },
    { path: "/owner/customers", label: "nav.customers", perm: "customers.view", group: "customers", icon: "customers" },
    { path: "/owner/debts", label: "dr.nav.debts", perm: "customers.view", group: "customers", icon: "wallet" },
    { path: "/owner/membership", label: "nav.membership", perm: "membership.manage", group: "customers", icon: "membership" },
    { path: "/owner/member-centre", label: "nav.memberCentre", perm: "customers.view", group: "customers", icon: "wallet" },
    // (Task V) customer subscriptions — recurring billing via the shop's Stripe account (Task W: Full plan only)
    { path: "/owner/subscriptions", label: "tv.nav", perm: "subscriptions.view", feature: "subscriptions", group: "customers", icon: "repeat" },
    { path: "/owner/campaigns", label: "nav.campaigns", perm: "marketing.manage", group: "customers", icon: "campaigns" },
    // (Task WA-center) the shop's own linked WhatsApp: connect, booking texts, contacts, broadcasts, activity (owner + manager)
    { path: "/owner/whatsapp", label: "wac.nav", perm: "marketing.manage", group: "customers", icon: "whatsapp" },
    { path: "/owner/services", label: "nav.services", perm: "catalog.manage", group: "catalog", icon: "services" },
    { path: "/owner/packages", label: "nav.packages", perm: "catalog.manage", feature: "packages", group: "catalog", icon: "ticket" },
    { path: "/owner/sales", label: "nav.sales", perm: "sales.create", group: "catalog", icon: "receipt" },
    { path: "/owner/team", label: "nav.team", perm: "team.view", group: "team", icon: "team" },
    { path: "/owner/attendance", label: "to.nav.attendance", perm: "attendance.manage", feature: "attendance", group: "team", icon: "clock" },
    { path: "/owner/inventory", label: "nav.inventory", perm: "inventory.manage", feature: "inventory", group: "stock", icon: "box" },
    // (Task T) suppliers / purchase orders / payables = the Supplier system add-on (included in Full)
    { path: "/owner/purchase-orders", label: "nav.purchaseOrders", perm: "purchasing.manage", feature: "suppliers", group: "stock", icon: "clipboard" },
    { path: "/owner/suppliers", label: "nav.suppliers", perm: "purchasing.manage", feature: "suppliers", group: "stock", icon: "truck" },
    { path: "/owner/payables", label: "nav.payables", perm: "finance.view", feature: "suppliers", group: "stock", icon: "payables" },
    { path: "/owner/daily-close", label: "nav.dailyClose", perm: "daily_close.view", group: "finance", icon: "dailyClose" },
    { path: "/owner/expenses", label: "nav.expenses", perm: "expenses.manage", group: "finance", icon: "expenses" },
    // (Task FF) 工资管理 / Payroll lives under 财务 / Finance
    { path: "/owner/payroll", label: "nav.payroll", perm: "payroll.rules", feature: "payroll", group: "finance", icon: "payroll" },
    { path: "/owner/reports", label: "nav.reports", perm: "finance.view", group: "finance", icon: "reports" },
    { path: "/owner/website", label: "nav.website", perm: "website.manage", feature: "website", group: "online", icon: "globe" },
    { path: "/owner/shop", label: "nav.shop", perm: "shop.manage", feature: "shop", group: "online", icon: "store" },
    // Task U: every branch side by side (sales, stock, team, transfers) and what branches share
    { path: "/owner/branches", label: "tu.nav", perm: "branches.manage", group: "pinned", icon: "building" },
    { path: "/owner/settings", label: "nav.settings", perm: "settings.manage", group: "pinned", icon: "settings" },
  ],
  checkout: IS_NATIVE ? NATIVE_NAV : [
    { path: "/checkout/pos", label: "nav.pos", perm: "sales.create", icon: "register" },
    { path: "/checkout/rotation", label: "rot.nav", perm: "appointments.view", industry: "massage", icon: "repeat" },
    { path: "/checkout/appointments", label: "nav.appointments", perm: "appointments.view", icon: "calendar" },
    { path: "/checkout/customers", label: "nav.customers", perm: "customers.view", icon: "customers" },
    { path: "/checkout/member-centre", label: "nav.memberCentre", perm: "customers.view", icon: "wallet" },
    { path: "/checkout/subscriptions", label: "tv.nav", perm: "subscriptions.view", feature: "subscriptions", icon: "repeat" },
    { path: "/checkout/cash", label: "nav.cash", perm: "cash_session.operate", icon: "cash" },
    { path: "/checkout/sales", label: "nav.sales", perm: "sales.create", icon: "receipt" },
    { path: "/checkout/online-orders", label: "nav.onlineOrders", perm: "sales.create", feature: "shop", icon: "orders" },
    { path: "/checkout/approvals", label: "nav.approvals", perm: "sales.create", icon: "approvals" },
    { path: "/checkout/daily-close", label: "nav.dailyClose", perm: "daily_close.view", icon: "dailyClose" },
  ],
  employee: IS_NATIVE ? [] : [
    { path: "/employee/day", label: "nav.myDay", perm: "appointments.view", icon: "sun" },
    { path: "/employee/payslips", label: "nav.myPayroll", perm: "payroll.own", feature: "payroll", icon: "payroll" },
    { path: "/employee/claims", label: "ee.me.claims", perm: "payroll.own", feature: "payroll", icon: "expenses" },
    { path: "/employee/leave", label: "ee.me.leave", perm: "payroll.own", feature: "payroll", icon: "calendar" },
    { path: "/employee/bank", label: "ee.me.bank", perm: "payroll.own", feature: "payroll", icon: "wallet" },
    // Task U: staff portal — only the signed-in person's own figures
    { path: "/employee/performance", label: "tu.me.perf", perm: "payroll.own", icon: "reports" },
    { path: "/employee/customers", label: "tu.me.customers", perm: "payroll.own", icon: "customers" },
    { path: "/employee/attendance", label: "tu.me.attendance", perm: "payroll.own", feature: "attendance", icon: "clock" },
    { path: "/employee/statements", label: "tu.me.statements", perm: "payroll.own", icon: "receipt" },
  ],
  platform: IS_NATIVE ? [] : [{ path: "/platform/shops", label: "kk.nav", icon: "customers" }, { path: "/platform/tenants", label: "kk.navOps", icon: "building" }, { path: "/platform/app-updates", label: "tq.upd.nav", icon: "globe" },
    // Task S: the official Senvia POS marketing website (官网设置) and its contact / demo requests
    { path: "/platform/plans", label: "tt.pf.nav", icon: "payables" }, { path: "/platform/website", label: "ts.nav.site", icon: "store" }, { path: "/platform/leads", label: "ts.nav.leads", icon: "customers" },
    // Task HH: platform AI (Senvia 助手 + AI import): models, caps, usage per shop
    { path: "/platform/ai", label: "hh.pa.nav", icon: "reports" },
    // Task LL: the platform's own ad tracking (Meta Pixel + Conversions API, Google tag, TikTok) on senviapos.com
    { path: "/platform/ads", label: "ll.nav", icon: "reports" },
    // Task NN: 投诉与建议 inbox (shops' complaints, suggestions, bug reports)
    { path: "/platform/feedback", label: "nn.nav", icon: "approvals" },
    // Task EM: e-mail centre (automated e-mail templates, broadcasts to shop owners, sent log, provider)
    { path: "/platform/email", label: "em.nav", icon: "globe" },
    // Task SF: Senvia service fee on shops' membership subscriptions (fee table, notice, fees collected)
    { path: "/platform/service-fee", label: "sf.nav", icon: "reports" }],
};

/** Extra routes reachable from inside pages (not in the sidebar), with their permission. */
export const HIDDEN_ROUTES: Record<string, Permission | undefined> = IS_NATIVE ? {
  "/checkout/device": undefined,
  // (Task AN) cashier sections + the owner's all-branch dashboard (the pages / API also check the role)
  "/checkout/package-customers": "customers.view",
  "/checkout/subscriptions": "subscriptions.view",
  "/checkout/hq": "finance.view",
  // (Task NN) 投诉与建议 under the Me tab
  "/checkout/me/feedback": "feedback.view",
} : {
  // (Task NN) 投诉与建议 / Feedback: owner portal (owner + managers) and staff portal (staff / cashiers allowed in the matrix)
  "/owner/feedback": "feedback.view",
  "/employee/feedback": "feedback.view",
  "/owner/cash": "cash_session.operate",
  // Task K: bulk customer import (owner / manager)
  "/owner/customers/import": "customers.import",
  "/owner/addons": "settings.manage",
  "/owner/messages": "settings.manage",
  "/owner/branches": "branches.view",
  "/owner/supplier-invoices": "purchasing.manage",
  "/owner/stripe/return": "billing.manage",
  "/owner/stripe/refresh": "billing.manage",
  // native app only: this device (app lock, notifications, server) — no permission needed
  "/checkout/device": undefined,
  // (Task AN) 配套顾客列表 (cashier front desk)
  "/checkout/package-customers": "customers.view",
};

/** (Task NN) where 投诉与建议 lives for this workspace (store app: under Me; the owner path never ships in the app bundle) */
export const feedbackPath = (ws: Workspace): string => (IS_NATIVE ? "/checkout/me/feedback" : ws === "owner" ? "/owner/feedback" : "/employee/feedback");

export const WS_LABEL: Record<Workspace, MessageKey> = { owner: "ws.owner", checkout: "ws.checkout", employee: "ws.employee", platform: "ws.platform" };
export const WS_HOME: Record<Workspace, string> = IS_NATIVE ? { owner: "/", checkout: "/", employee: "/", platform: "/" } : { owner: "/owner/overview", checkout: "/checkout/pos", employee: "/employee/day", platform: "/platform/shops" };

export function allowedWorkspaces(me: MeResponse, native = IS_NATIVE): Workspace[] {
  // native store app: every member of a salon (any role) uses the one store workspace; no platform admin
  if (native) return me.membership ? [...NATIVE_WORKSPACES] : [];
  return workspacesFor(me.membership?.role ?? null, me.user.isPlatformAdmin);
}

/** (Task U, rule D) the register is only for logins that may ring up sales (cashier logins / transition / no cashier yet) */
// The owner-portal path never ships in the store app bundle (check-native-bundle forbids "/owner/").
const POS_PATHS = new Set(IS_NATIVE ? ["/checkout/pos"] : ["/owner/pos", "/checkout/pos"]);
const permitted = (me: MeResponse, i: { perm?: Permission; path?: string }) => (!i.perm || (me.permissions[i.perm] ?? "none") !== "none") && !(i.path && POS_PATHS.has(i.path) && me.checkout?.canSell === false);

const featureOn = (me: MeResponse, i: NavItem) => (!i.feature || me.features.includes(i.feature)) && (!i.industry || (me.tenant?.businessTypes ?? []).includes(i.industry));
const isCashier = (me: MeResponse) => me.membership?.role === "cashier";
/** (Task AN) web front desk for a CASHIER login: 收银 · 预约 · 顾客 · 配套顾客列表 · 订阅顾客列表, then the rest */
const WEB_CASHIER_ORDER = ["/checkout/pos", "/checkout/rotation", "/checkout/appointments", "/checkout/customers", "/checkout/package-customers", "/checkout/subscriptions"];
function cashierOrder(items: NavItem[], order: string[]): NavItem[] {
  const rank = (p: string) => { const i = order.indexOf(p); return i < 0 ? order.length : i; };
  return items.map((it, idx) => ({ it, idx })).sort((a, b) => rank(a.it.path) - rank(b.it.path) || a.idx - b.idx).map((x) => x.it);
}

/** Sidebar items: permitted for the role AND (no add-on needed OR the add-on is active). */
export function visibleNav(me: MeResponse, ws: Workspace): NavItem[] {
  const items = NAV[ws].filter((i) => permitted(me, i) && featureOn(me, i));
  if (!IS_NATIVE && ws === "checkout" && isCashier(me)) {
    const extra = CASHIER_EXTRA.filter((i) => i.path === "/checkout/package-customers" && permitted(me, i) && featureOn(me, i) && !items.some((x) => x.path === i.path));
    return cashierOrder([...items, ...extra], WEB_CASHIER_ORDER);
  }
  return items;
}

/**
 * (Task W) Full-plan-only modules (subscriptions, attendance) the salon doesn't have: the owner / manager sidebar still
 * lists them (locked, "Full" badge) so the owner finds them where they look — the page explains the Full plan.
 * Front desk, staff portal and the store app hide them.
 */
export function lockedNav(me: MeResponse, ws: Workspace): NavItem[] {
  if (ws !== "owner" || IS_NATIVE) return [];
  return NAV.owner.filter((i) => isFullPlanOnly(i.feature) && !me.features.includes(i.feature!) && permitted(me, i)).map((i) => ({ ...i, locked: true }));
}

/** (Task O) The store app's sections for this member (exactly the permitted NATIVE_NAV items, in order). */
export function nativeNavFor(me: MeResponse, mode: OwnerMode = ownerMode()): NavItem[] {
  // (Task AN) the owner's all-branch dashboard (default for the owner role; "switch to cashier mode" shows the store tabs)
  if (me.membership?.role === "owner" && mode === "hq") return OWNER_APP_NAV.filter((i) => featureOn(me, i));
  const base = NATIVE_NAV.filter((i) => permitted(me, i) && featureOn(me, i));
  // (Task AN) cashier logins only: their own order + 配套顾客列表 / 订阅顾客列表; every other role is unchanged
  if (!isCashier(me)) return base;
  const extra = CASHIER_EXTRA.filter((i) => permitted(me, i) && featureOn(me, i));
  return cashierOrder([...base, ...extra], CASHIER_ORDER);
}
/** Native start page: Home (the Performance dashboard) for everyone; the owner's all-branch 首页 in owner mode. */
export const nativeHome = (me: MeResponse) => nativeNavFor(me)[0]?.path ?? "/checkout/device";

/**
 * (Task P) Bottom tab bar for this member: up to MAX_TABS visible tabs; when there are more sections, the first four
 * stay as tabs and the rest (plus This device) go under "More". Staff: Home / Bookings / Clock / Photos (no More).
 */
export function nativeTabsFor(me: MeResponse, mode: OwnerMode = ownerMode()): { tabs: NavItem[]; more: NavItem[] } {
  const items = nativeNavFor(me, mode);
  const owner = me.membership?.role === "owner";
  if (owner && mode === "hq") {
    // (Task AN) the five dashboard tabs + 更多: the store sections and "switch to cashier mode"
    const store = nativeNavFor(me, "store").filter((i) => i.path !== "/checkout/home");
    return { tabs: items, more: [...store, { path: MODE_SWITCH, label: "an.mode.toStore", icon: "register" }, NATIVE_DEVICE] };
  }
  const back: NavItem[] = owner ? [{ path: MODE_SWITCH, label: "an.mode.toOwner", icon: "overview" }] : [];
  if (items.length <= MAX_TABS && !back.length) return { tabs: items, more: [] };
  return { tabs: items.slice(0, MAX_TABS - 1), more: [...items.slice(MAX_TABS - 1), ...back, NATIVE_DEVICE] };
}

/** The page an add-on opens (its main module), in Add-ons page order. */
export const FEATURE_HOME: Record<string, string> = {
  packages: "/owner/packages", inventory: "/owner/inventory", payroll: "/owner/payroll", website: "/owner/website", shop: "/owner/shop", suppliers: "/owner/purchase-orders",
};

/** Modules the role could use but the salon hasn't added — listed on the Add-ons page. */
export function addonItems(me: MeResponse, ws: Workspace = "owner"): NavItem[] {
  // one entry per add-on (Inventory also brings Suppliers / Purchase orders / Payables; Online shop also brings Online orders)
  const homes = new Set(Object.values(FEATURE_HOME));
  // (Task W) Full-plan-only modules are not add-ons (they show as locked sidebar entries instead)
  const items = NAV[ws].filter((i) => permitted(me, i) && !!i.feature && !me.features.includes(i.feature) && !isFullPlanOnly(i.feature));
  const out: NavItem[] = [];
  for (const f of Object.keys(FEATURE_HOME)) { const it = items.find((i) => i.feature === f && homes.has(i.path)) ?? items.find((i) => i.feature === f); if (it) out.push(it); }
  for (const i of items) if (!Object.prototype.hasOwnProperty.call(FEATURE_HOME, i.feature!) && !out.some((o) => o.feature === i.feature)) out.push(i);
  return out;
}

export interface NavSection { id: NavGroup; label: MessageKey | null; items: NavItem[] }

/** Visible owner items arranged into sections (daily first, headed groups, then pinned); empty sections are dropped. */
export function groupNav(items: NavItem[]): NavSection[] {
  const sections: NavSection[] = [
    { id: "daily", label: null, items: items.filter((i) => (i.group ?? "daily") === "daily") },
    ...NAV_GROUPS.map((g) => ({ id: g.id, label: g.label, items: items.filter((i) => i.group === g.id) })),
    { id: "pinned", label: null, items: items.filter((i) => i.group === "pinned") },
  ];
  return sections.filter((s) => s.items.length > 0);
}

/** Pages reached from inside a module (not in the sidebar) still light up their module's group. */
const GROUP_ALIASES: Record<string, NavGroup> = IS_NATIVE ? {} : { // store app: no owner sidebar (and no owner paths in the bundle)
  "/owner/cash": "daily", "/owner/messages": "customers", "/owner/supplier-invoices": "stock", "/owner/addons": "pinned", "/owner/branches": "pinned",
};

/** The group containing the current page (longest matching item path), or null. */
export function activeGroup(pathname: string, items: NavItem[] = NAV.owner): NavGroup | null {
  const under = (p: string) => pathname === p || pathname.startsWith(p + "/");
  const best = items.filter((i) => under(i.path)).sort((a, b) => b.path.length - a.path.length)[0];
  if (best) return best.group ?? "daily";
  const alias = Object.keys(GROUP_ALIASES).find(under);
  return alias ? GROUP_ALIASES[alias]! : null;
}

/** Quick search: items whose label (in the current language) contains the query, case- and space-insensitive. */
export function filterNav(items: NavItem[], query: string, label: (i: NavItem) => string): NavItem[] {
  const norm = (x: string) => x.toLocaleLowerCase().replace(/\s+/g, "");
  const q = norm(query);
  if (!q) return items;
  return items.filter((i) => norm(label(i)).includes(q));
}

/** Whether the owner sees the single "Add-ons" entry. */
export const showAddonsEntry = (me: MeResponse, ws: Workspace) => ws === "owner" && permitted(me, { perm: "settings.manage" });
